import type { Translation } from '@/types/translation';

// German translation file.

const deTranslation: Translation = {
  common: {
    save: 'Speichern',
    close: 'Schließen',
    search: 'Suchen',
    cancel: 'Abbrechen',
    back: 'Zurück',
    continue: 'Weiter',
    submit: 'Absenden',
    confirm: 'Bestätigen',
    delete: 'Löschen',
    save_changes: 'Änderungen speichern',
    loading: 'Lädt...',
  },
  pages: {
    account: {
      tabs: {
        dashboard: 'Instrumententafel',
        fleet_overview: 'Flottenübersicht',
        unit_overview: 'Alle Einheiten',
        campaigns: 'Kampagnen',
        members: 'Mitglieder',
      },
      dashboard: {
        active_fault_codes: {
          title: 'Aktive Fehlercodes',
          units_title: 'Einheiten mit aktiven Fehlercodes',
        },
        connectivity: {
          title: 'Konnektivität',
          units_title: 'Konnektivitätsstatus',
        },
        fuel_consumption: {
          title: 'Kraftstoffverbrauch',
          details_title: 'Details zum Kraftstoffverbrauch',
        },
      },
      fleet_overview: {
        pinned_fleets: 'Angeheftete Flotten',
        other_fleets: 'Andere Flotten',
        pin: 'Flotte anheften',
        unpin: 'Flotte lösen',
        edit: 'Flotte bearbeiten',
        remove: 'Flotte entfernen',
      },
      unit_overview: {
        title: 'Einheiten',
        unverified_ownership: 'Unbestätigter Besitz',
        add_to_fleet: 'Zur Flotte hinzufügen',
      },
      campaigns: {
        active_table: {
          title: 'Aktive Kampagnen',
          download_campaigns: 'Kampagnen herunterladen (xlsx)',
          empty_state: {
            title: 'Sie haben keine aktiven Kampagnen für diese Einheit',
          },
        },
        performed_table: {
          title: 'Durchgeführte Kampagnen',
          empty_state: {
            title: 'Keine durchgeführten Kampagnen verfügbar',
          },
        },
      },
      members: {
        title: 'Mitglieder',
        remove_members_from_account: 'Entfernen Sie {{selectedRows}} Mitglieder vom Konto',
      },
    },
    fleet: {
      tabs: {
        dashboard: 'Instrumententafel',
        unit_overview: 'Alle Einheiten',
        campaigns: 'Kampagnen',
      },
      campaigns: {
        title: 'Aktive Kampagnen',
        download_campaigns: 'Kampagnen herunterladen (xlsx)',
        empty_state: {
          title: 'Sie haben keine aktiven Kampagnen für diese Flotte',
        },
      },
    },
    unit: {
      tabs: {
        specifications: 'Spezifikationen',
        campaigns: 'Kampagnen',
        coverage: 'Garantie & Abdeckung',
        maintenance_history: 'Registrierte Services',
        manuals_handbooks: 'Handbücher & Anleitungen',
        diagnostics_log: 'Diagnoseprotokoll',
        services: 'Digitale Dienste',
        settings: 'Einstellungen',
      },
      spare_parts: 'Ersatzteile',
      manuals_selection_description: 'Für welches Produkt möchten Sie Handbücher und Anleitungen finden.',
      specifications: {
        title: 'Spezifikationen',
      },
      campaigns: {
        active_table: {
          title: 'Aktive Kampagnen',
          empty_state: {
            title: 'Sie haben keine aktiven Kampagnen für diese Einheit',
          },
        },
        performed_table: {
          title: 'Durchgeführte Kampagnen',
          empty_state: {
            title: 'Keine durchgeführten Kampagnen verfügbar',
          },
        },
      },
      coverage: {
        cta: {
          heading: 'Informationen zu Garantie & Abdeckung',
          message: 'Bei Fragen zu Garantie & Abdeckung wenden Sie sich an Ihren Volvo Penta oder OEM-Händler.',
          find_dealer: 'Finden Sie Ihren nächstgelegenen Händler',
          learn_more: 'Erfahren Sie mehr',
        },
        empty_state: {
          title: 'Für diese Einheit wurden keine Garantie und/oder Abdeckung gefunden',
        },
      },
      publications: {
        select_menu: {
          category: 'Kategorie',
          designation: 'Bezeichnung',
          product_type: 'Produkttyp',
          language: 'Sprache',
        },
        empty_state: {
          title: 'Für diese Einheit sind keine Veröffentlichungen verfügbar',
        },
      },
      services: {
        title: 'Verfügbare Dienstleistungen',
        all_services: {
          title: 'Alle Dienste aktivieren',
        },
        list: {
          ActiveMonitoring: {
            Marine: {
              title: 'Extended',
              description:
                'Profitieren Sie von allen Vorteilen von Core sowie automatischen E-Mail-Benachrichtigungen direkt an Ihren ausgewählten Händler.',
              checklist: [
                'Benachrichtigen Sie Ihren Händler sofort, wenn Ihr System einen kritischen Fehlercode erkennt',
                'Ermöglichen Sie Ihrem Händler, sofortige Korrekturmaßnahmen vorzubereiten',
              ],
            },
            Industrial: {
              title: 'Aktive Überwachung',
              description:
                'Active Monitoring hilft Kunden, ihre Volvo Penta-Produkte am Laufen zu halten, indem Probleme frühzeitig erkannt, schnellerer Service unterstützt und klare Einblicke in Zustand und Nutzung des Produkts gegeben werden.\n\nActive Monitoring ermöglicht außerdem Remote Software Updates, einen Service zur Installation von Softwarekampagnen over-the-air, wann immer es Ihnen passt.',
              checklist: [
                'Höhere Betriebszeit durch frühzeitige Warnungen und proaktiven Support',
                'Schnellerer, besser vorbereiteter Service dank vorab verfügbarer relevanter Informationen',
              ],
            },
          },
          ActiveMonitoringCore: {
            Marine: {
              title: 'Core',
              description:
                'Teilen Sie grundlegende Fahrzeugdaten mit Ihrem Händler, um bei Wartung oder unerwarteten Problemen schneller Unterstützung zu erhalten.',
              checklist: [
                'Lassen Sie Ihren Händler den Produktzustand überwachen und Betriebsstunden verfolgen',
                'Ermöglichen Sie Ihrem Händler, Fehlercodes sofort zu analysieren, um den Service proaktiv zu planen',
              ],
            },
            Industrial: {
              title: 'Connected Services Core',
              description:
                'Core ermöglicht es dem Volvo Penta-Händler, das Volvo Penta-Produkt zu überwachen. <ActiveMonitoringCore>Weitere Details finden Sie in der Servicebeschreibung für Connected Services Core.</ActiveMonitoringCore>',
              checklist: [],
            },
          },
          ActiveMonitoringEcWifi: {
            Marine: {
              title: 'Extended',
              description:
                'Ermöglichen Sie Ihrem Händler, Fahrzeugdaten zu verfolgen und sofortige Benachrichtigungen bei kritischen Fehlercodes zu erhalten, um sofortige Korrekturmaßnahmen vorzubereiten.',
              checklist: [
                'Benachrichtigen Sie Ihren Händler sofort, wenn Ihr System einen kritischen Fehlercode erkennt',
                'Ermöglichen Sie Ihrem Händler, sofortige Korrekturmaßnahmen vorzubereiten',
              ],
            },
            Industrial: {
              title: 'Extended',
              description:
                'Ermöglichen Sie Ihrem Händler, Fahrzeugdaten zu verfolgen und sofortige Benachrichtigungen bei kritischen Fehlercodes zu erhalten, um sofortige Korrekturmaßnahmen vorzubereiten.',
              checklist: [
                'Benachrichtigen Sie Ihren Händler sofort, wenn Ihr System einen kritischen Fehlercode erkennt',
                'Ermöglichen Sie Ihrem Händler, sofortige Korrekturmaßnahmen vorzubereiten',
              ],
            },
          },
          Penta_Position: {
            Marine: {
              title: 'Standortdienste',
              description:
                'Location Services hilft Kunden, ihre vernetzten Volvo Penta-Produkte über eine Kartenansicht in Volvo Penta Connect schnell zu lokalisieren und zu verwalten – für ein einfacheres Servicemanagement und eine bessere Flottenübersicht.',
              checklist: [
                'Bessere Flottenübersicht mit einer Karte, die den letzten Standort der vernetzten Einheiten anzeigt',
                'Schnellere Servicereaktion durch einfachen Zugriff auf den Standort der Einheit, einschließlich Betriebsstunden und Fehlercodes',
              ],
            },
            Industrial: {
              title: 'Standortdienste',
              description:
                'Location Services hilft Kunden, ihre vernetzten Volvo Penta-Produkte über eine Kartenansicht in Volvo Penta Connect schnell zu lokalisieren und zu verwalten – für ein einfacheres Servicemanagement und eine bessere Flottenübersicht.',
              checklist: [
                'Bessere Flottenübersicht mit einer Karte, die den letzten Standort der vernetzten Einheiten anzeigt',
                'Schnellere Servicereaktion durch einfachen Zugriff auf den Standort der Einheit, einschließlich Betriebsstunden und Fehlercodes',
              ],
            },
          },
        },
        request_sent: 'Anfrage gesendet',
        empty_state: {
          title: 'Service-Bereitstellung nicht verfügbar',
          message: 'Die Bereitstellung digitaler Services ist für diese Einheit nicht verfügbar.',
        },
        active_services_title: 'Aktive Dienste',
        available_services_title: 'Verfügbare Dienste',
        activate_button: 'Dienst aktivieren',
        terminate_button: 'Kündigen',
        read_more_button: 'Mehr erfahren',
        requires_prefix: 'Erfordert {{serviceName}}',
      },
      settings: {
        unit_settings: {
          title: 'Einheitseinstellungen',
          unit_name: 'Einheitenname',
          home_port: 'Heimathafen',
          operating_location: 'Einsatzort',
          upload_image: 'Bild hochladen',
          add_files: 'Dateien hinzufügen',
          helperText: 'PNG- oder JPG-Datei (max. 3 MB)',
        },
        network_settings: {
          title: 'Netzwerkeinstellungen',
          empty_state: {
            title: 'Für diese Einheit sind keine aktiven Netzwerke vorhanden',
            message:
              'Die Netzwerkeinstellungen für diese Einheit werden hier angezeigt, sobald sie konfiguriert wurden.',
          },
          updating_title: 'Netzwerkeinstellungen werden aktualisiert',
          updating_description: 'Bitte warten Sie, während wir die neueste Netzwerkkonfiguration abrufen.',
        },
      },
      maintenance: {
        service_protocol_for_chassi: 'Serviceprotokoll für Fahrgestell {{chassisNumber}}',
        download_pdf: 'PDF herunterladen',
        service: 'Service {{type}}',
        default_service: 'Antriebsstrang {{chassisSeries}} {{chassisNumber}}',
        general_comment: 'Allgemeiner Servicekommentar',
        no_comment: 'Keine allgemeinen Kommentare für dieses Serviceprotokoll.',
        periodic_tag: 'Alle {{months}} Monate / {{engineHours}} h',
        non_periodic_month_tag: 'Bei {{months}} Monaten',
        non_periodic_hours_tag: 'Bei {{engineHours}} h',
        empty_state: {
          title: 'Keine Wartungshistorie verfügbar',
          message:
            'Serviceprotokolle für diesen Motor werden hier angezeigt, sobald sie von einem Volvo Penta-Händler registriert wurden.',
        },
        unverified_ownership_notice:
          'Einige Informationen sind möglicherweise eingeschränkt, da der Besitz dieser Einheit noch nicht verifiziert wurde. Wenden Sie sich an Ihren autorisierten Volvo Penta-Händler, um die Verifizierung abzuschließen.',
      },
    },
    settings: {
      application_settings: 'Anwendungseinstellungen',
      account_name: 'Flottenkontoname',
      notifications: 'Benachrichtigungen',
      email: 'E-Mail',
      email_notifications: 'E-Mail-Benachrichtigungen aktivieren',
      sms: 'SMS',
      sms_notifications: 'SMS-Benachrichtigungen aktivieren',
    },
    support: {
      title: 'Häufig gestellte Fragen',
      description: 'Finden Sie Kontaktinformationen sowie allgemeine Informationen zu Volvo Penta Connect.',
      professional_support: {
        tag: 'Professionelle Unterstützung',
        title: 'Die ultimativen Experten - zu Ihren Diensten',
        description:
          'Kontaktieren Sie einen autorisierten Volvo Penta-Händler als erste Anlaufstelle, wenn Sie Hilfe bei Motorwartung, Reparaturen, Ersatzteilen oder anderen Unterstützungsleistungen benötigen - für Freizeit- und Nutzfahrzeuge.',
      },
      emergency_support: {
        tag: 'Notfallunterstützung',
        title: 'Volvo Penta Action Service',
        description:
          'Ein Motorausfall kommt selten vor, aber wenn er eintritt, steht der Action Service bereit, um Sie mit dem nächstgelegenen Volvo Penta-Partner in Kontakt zu bringen. Wir bieten Unterstützung in mehreren Sprachen, rund um die Uhr, das ganze Jahr über.',
      },
      platform_support: {
        tag: 'Plattformunterstützung',
        title: 'Volvo Penta Connect Support',
        description:
          'Kontaktieren Sie unser dediziertes Team, um Hilfe bei Ihrem Volvo Penta Connect-Konto und den Funktionen zu erhalten. Wir helfen Ihnen, Ihre Motordaten und Konnektivität einfach zu verwalten.',
      },
      getting_started: {
        title: 'Erste Schritte',
        question_1: {
          title: 'Für wen ist Volvo Penta Connect gedacht?',
          body: 'Volvo Penta Connect ist ein Kundenportal, das Ihnen einen Überblick über Ihre Einheit(en) bietet und den Zugriff auf relevante Informationen wie Handbücher, Kampagnen, Garantieinformationen und mehr ermöglicht.',
        },
        question_2: {
          title: 'Was muss ich zuerst tun, um dieses Produkt zu nutzen?',
          body: 'Sie müssen eine Einheit in Ihrem Konto registrieren. Sobald eine Einheit hinzugefügt wurde, können Sie die Einheitsdaten überwachen, Over-the-Air-Software-Updates installieren und auf Handbücher und Anleitungen zugreifen, die mit dieser Einheit verknüpft sind.',
          link: { href: 'https://www.volvopenta.com/connect/units', label: 'Einheit hinzufügen' },
        },
        question_3: {
          title: 'Kann ich dieses Produkt nutzen, ohne eine Einheit hinzuzufügen?',
          body: 'Nein. Volvo Penta Connect ist auf Ihre Einheiten ausgelegt. Handbücher, Motordaten und digitale Dienste sind alle mit einer bestimmten Einheit verknüpft. Wenn Sie keine Einheit besitzen oder registrieren möchten, erfüllt dieses Produkt nicht Ihre Anforderungen.',
          link: { href: 'https://www.volvopenta.com/connect/units', label: 'Einheit hinzufügen' },
        },
        question_4: {
          title: 'Wie registriere ich meinen Motor?',
          body: 'Gehen Sie zur Einheitenliste und wählen Sie Einheit hinzufügen. Suchen Sie nach der Seriennummer und füllen Sie das Registrierungsformular aus. Einige neuere Industriemotoren verfügen über einen vorinstallierten QR-Code am Motor. Wenn verfügbar, können Sie diesen Code scannen, um den Registrierungsprozess zu starten.',
        },
        question_5: {
          title: 'Welche Informationen muss ich angeben?',
          body: 'Beginnen Sie mit der Registrierung Ihrer Einheit in Volvo Penta Connect. Gehen Sie zur Einheitenliste und wählen Sie Einheit hinzufügen, suchen Sie dann nach der Seriennummer der Einheit und füllen Sie das Registrierungsformular aus.<br /><br />Sobald die Einheit registriert ist, erhalten Sie Zugriff auf Informationen wie Handbücher, Ersatzteile, Kampagnen, Garantieinformationen und digitale Dienste, die mit dieser Einheit verknüpft sind.',
          link: { href: 'https://www.volvopenta.com/connect/units', label: 'Einheit hinzufügen' },
        },
        question_6: {
          title: 'Was kann ich tun, sobald meine Einheit registriert ist?',
          body: 'Mit einer registrierten Einheit können Sie:<br /><br /><ul><li>- Einheiten nach Fahrzeugtyp oder Anwendungsfall in Flotten gruppieren</li><li>- Den Status der Einheit überwachen</li><li>- Over-the-Air-Software-Updates installieren</li><li>- Auf handbuch- und einheitsspezifische Anleitungen zugreifen</li></ul>',
          link: { href: 'https://www.volvopenta.com/connect/units', label: 'Einheitenübersicht' },
        },
        question_7: {
          title: 'Wo finde ich Handbücher & Anleitungen?',
          body: 'Handbücher und Anleitungen werden verfügbar, sobald eine Einheit zu Ihrem Konto hinzugefügt wurde, sodass wir Dokumentationen anzeigen können, die zu Ihrer spezifischen Einheit passen.',
          link: { href: 'https://www.volvopenta.com/connect/units', label: 'Einheit hinzufügen' },
        },
        question_8: {
          title: 'Wie füge ich einen Benutzer zu meinem Flottenkonto hinzu?',
          body: 'Um einen neuen Benutzer hinzuzufügen, müssen Sie ihn von der Mitgliederseite aus einladen.<br /><br /><ul><li>1. Gehen Sie zu Mitglieder</li><li>2. Wählen Sie Mitglied hinzufügen</li><li>3. Erstellen Sie einen Einladungslink und kopieren Sie ihn</li><li>4. Teilen Sie den Link mit der Person, die Sie einladen möchten</li><li>5. Sobald die Einladung akzeptiert wurde, weisen Sie dem neuen Mitglied eine Rolle zu</li></ul><br/>Nur Benutzer mit Berechtigung zur Verwaltung von Mitgliedern können Einladungen senden und Rollen zuweisen.',
          link: { href: 'https://www.volvopenta.com/connect/members', label: 'Mitglieder' },
        },
        question_9: {
          title: "Warum wird meine Einheit als 'Unverified owner' gekennzeichnet?",
          body: 'Neu registrierte Einheiten werden vorübergehend als Unverified owner gekennzeichnet, bis der Besitz bestätigt wurde.<br /><br />Der Besitz kann von einem autorisierten Volvo Penta-Händler oder unserem Support-Team überprüft werden. Nach der Überprüfung wird die Kennzeichnung automatisch entfernt.<br /><br />Wenn Sie Hilfe bei der Überprüfung benötigen, wenden Sie sich bitte an Ihren autorisierten Volvo Penta-Händler.',
        },
        question_10: {
          title: 'Warum kann ich meine Einheiten nicht sehen?',
          body: 'Ihre Einheiten werden möglicherweise aus mehreren Gründen nicht in Ihrem Konto angezeigt. Bitte überprüfen Sie diese häufigen Szenarien:<br /><br />E-Mail-Abgleich:<br/>Ihre Einheiten sind mit einer bestimmten E-Mail-Adresse verknüpft. Wenn Sie ein Konto mit einer anderen Adresse erstellt haben, werden die Einheiten nicht angezeigt. Sie können ein neues Konto mit der richtigen E-Mail-Adresse erstellen oder sich an einen autorisierten Volvo Penta-Händler wenden, um Ihre Einheit erneut zu registrieren.<br /><br />Ausstehende Registrierung:<br/>Wenn Sie eine neue Industrieeinheit hinzugefügt haben, ist diese möglicherweise noch nicht in unseren Aufzeichnungen. Diese Einheiten werden in Volvo Penta Connect erst angezeigt, wenn unser Support-Team den manuellen Registrierungsprozess abgeschlossen hat.<br /><br />Kontorollen:<br/>Ihr Zugriff auf bestimmte Einheiten hängt von Ihrer zugewiesenen Rolle ab. Wenn Sie glauben, dass Ihr Zugriff eingeschränkt ist, wenden Sie sich bitte an Ihren Flottenmanager, um sicherzustellen, dass Sie die richtige Kontorolle haben.',
        },
      },
      connectivity_telematics: {
        title: 'Konnektivität & Telematik',
        question_1: {
          title: 'Wie erkenne ich, ob meine Einheit verbunden ist?',
          body: 'Sie können den Verbindungsstatus einer Einheit neben ihrem Namen in der Einheitenliste und auf der Einheitenseite sehen.<br /><br /><GreenDot /> – Die Einheit ist online. Daten wurden in den letzten 2 Stunden empfangen.<br /><br /><GreyDot /> – Die Einheit ist offline. In den letzten 2 Stunden wurden keine Daten empfangen.<br /><br /><YellowDot /> – Die Konnektivität ist verfügbar, aber nicht vollständig eingerichtet.<br /><br />Kein Punkt – Die Einheit verfügt nicht über Konnektivitätsfunktionen. Sie kann für die Installation eines Telematik-Gateways (TGW) durch einen Händler in Frage kommen.<br /><br />Wenn Ihre Einheit offline ist, kann sie weiterhin normal verwendet werden, aber die Datenaktualisierungen werden fortgesetzt, sobald die Einheit wieder verbunden ist.',
        },
        question_2: {
          title: 'Warum wird der Konnektivitätsstatus meiner Einheit nicht angezeigt?',
          body: 'Einheiten ohne Statusanzeige haben kein Telematik-Gateway (TGW) installiert oder aktiviert. Diese Einheiten können durch die Installation eines TGW über einen Volvo Penta-Händler verbunden werden.',
        },
        question_3: {
          title: 'Was ist ein Telematik-Gateway (TGW)?',
          body: 'Ein Telematik-Gateway (TGW) ist ein auf der Einheit installiertes Hardwaregerät, das die Konnektivität und den Datentransfer zu Volvo Penta Connect ermöglicht.<br /><br />Ohne ein TGW kann die Einheit nicht digital überwacht werden.',
        },
        question_4: {
          title: 'Wie verbinde ich meine Einheit mit Volvo Penta Connect?',
          body: 'Um eine Einheit zu verbinden, müssen die folgenden Schritte abgeschlossen werden:<br /><br /><ul><li>- Die Einheit muss ein TGW installiert haben</li><li>- Das TGW muss von einem Händler oder OEM aktiviert werden</li><li>- Erforderliche digitale Dienste müssen aktiviert sein</li><li>- Der Eigentümer muss der Datenverwaltungsvereinbarung zustimmen</li></ul><br /><br />Nach Abschluss wird die Einheit mit dem Konnektivitätsstatus in Volvo Penta Connect angezeigt.',
        },
        question_5: {
          title: 'Welche Smartphones/Tablets sind mit der Volvo Penta Connect App kompatibel?',
          body: 'Für iOS-Geräte muss die Betriebssystemversion iOS 17 oder höher sein. Für Android-Geräte muss die Betriebssystemversion 9.0 oder höher sein.',
        },
        question_6: {
          title: 'Wer kann mir bei der Installation oder Aktivierung der Konnektivität helfen?',
          body: 'Ein Volvo Penta-Händler kann bei der TGW-Installation, -Aktivierung und -Serviceeinrichtung helfen.',
          link: {
            href: 'https://www.volvopenta.com/about-us/dealer-locator/',
            label: 'Einen Volvo Penta-Händler finden',
          },
        },
        question_7: {
          title: 'Wie richte ich Wi-Fi für meine Einheit ein?',
          body: 'Sie können Ihre Wi-Fi-Verbindung direkt in der Anwendung konfigurieren. Befolgen Sie diese Schritte, um loszulegen:<br /><br /><ul><li>- **Zu den Einstellungen navigieren:** Gehen Sie zum Wi-Fi-Konfigurationsbereich in Ihrem Dashboard.</li><li>- **Anleitung befolgen:** Verwenden Sie das Handbuch (PDF) für Schritt-für-Schritt-Anweisungen zur Durchführung Ihrer Einrichtung.</li><li>- **Einheit verbinden:** Nach der Konfiguration bleibt Ihre Einheit automatisch mit Ihrem digitalen Arbeitsbereich verbunden.</li></ul>',
          link: {
            href: 'https://www.volvopenta.com/connect/documents/Connect-to-WiFi-onboard.pdf',
            label: 'Bedienungsanleitung',
            icon: 'import',
          },
        },
      },
      campaigns_software_updates: {
        title: 'Kampagnen & Software-Updates',
        question_1: {
          title: 'Was ist eine Kampagne?',
          body: 'Eine Kampagne ist eine von Volvo Penta initiierte Maßnahme zur Sicherstellung der Produktqualität. Wenn eine Kampagne eine Ihrer registrierten Einheiten betrifft, werden Sie benachrichtigt und durch die erforderlichen Schritte geführt.',
          link: {
            href: 'https://www.volvopenta.com/connect/campaigns',
            label: 'Kampagnenübersicht',
          },
        },
        question_2: {
          title: 'Was muss ich tun, wenn ich eine aktive Kampagne habe?',
          body: 'Es hängt vom Kampagnentyp ab:<br /><br /><ul><li>- **Rückruf / Qualität / Service:** Kontaktieren Sie Ihren örtlichen Volvo Penta-Händler</li><li>- **Installation / OTA:** Diese Kampagnen können über die Luftschnittstelle mit der Volvo Penta App abgeschlossen werden. Laden Sie die App herunter und folgen Sie den Anweisungen in der App.</li></ul>',
          link: {
            href: 'https://www.volvopenta.com/about-us/dealer-locator/',
            label: 'Einen Händler finden',
          },
          ios: { href: '#', label: 'App Store' },
          android: { href: '#', label: 'Google Play' },
        },
        question_3: {
          title: 'Was muss ich vor der Installation einer Over-the-Air (OTA) Kampagne beachten?',
          body: 'Bevor Sie mit der OTA-Installation beginnen, stellen Sie sicher, dass:<br /><br /><ul><li>- Sie die richtige Einheit ausgewählt haben</li><li>- Bluetooth auf Ihrem Gerät aktiviert ist</li><li>- Sie sich innerhalb von 3 Metern der Einheit befinden</li><li>- Alle erforderlichen Voraussetzungen im Produkt erfüllt sind</li><li>- Die Einheit während der Installation eingeschaltet und ungestört bleibt</li></ul><br/>Wenn eine Anforderung nicht erfüllt ist, startet die Installation nicht.',
        },
        question_4: {
          title: 'Ich habe Probleme bei der Installation einer Over-the-Air (OTA) Kampagne.',
          body: 'Wenn die Installation fehlschlägt oder nicht startet:<br /><br /><ul><li>- Überprüfen Sie, ob Bluetooth aktiviert ist und Sie sich in der Nähe der Einheit befinden</li><li>- Bleiben Sie während der gesamten Installation innerhalb von 3 Metern</li><li>- Starten Sie die App neu und versuchen Sie es erneut</li><li>- Überprüfen Sie erneut, ob alle Voraussetzungen der Einheit erfüllt sind</li></ul><br/>Wenn das Problem weiterhin besteht, wenden Sie sich an den Support.',
        },
        question_5: {
          title: 'Wo kann ich meine abgeschlossenen Over-the-Air (OTA) Installationen sehen?',
          body: 'Sie können abgeschlossene OTA-Installationen auf der Seite der Einheit einsehen. Öffnen Sie die Einheit und gehen Sie zum Tab "Kampagnen", um die vollständige Installationshistorie zu sehen.',
        },
        question_6: {
          title: 'Kann eine schlechte Verbindung die OTA-Installation beeinträchtigen?',
          body: 'Ja. Eine OTA-Installation kann fehlschlagen, wenn die Einheit eine instabile oder keine Mobilfunk- oder Wi-Fi-Verbindung hat. Stellen Sie sicher, dass die Einheit eine zuverlässige Verbindung hat und während der gesamten Installation verbunden bleibt.',
        },
        question_7: {
          title: 'Warum muss meine Einheit regelmäßig verbunden sein?',
          body: 'Regelmäßige Verbindung hilft sicherzustellen, dass Ihre Einheit für Software-Updates bereit ist. Wenn eine Einheit längere Zeit nicht verbunden war, können erforderliche Daten oder Vorbereitungen fehlen, was eine OTA-Installation verzögern oder blockieren kann.',
        },
        question_8: {
          title: 'Was passiert, wenn ich eine Kampagne nicht rechtzeitig abschließe?',
          body: 'Kampagnen werden nicht automatisch installiert. Wenn eine Kampagne nicht innerhalb des angegebenen Zeitrahmens abgeschlossen wird, ist es möglicherweise nicht mehr möglich, sie durchzuführen. Befolgen Sie immer die angezeigten Anweisungen und schließen Sie die Kampagne innerhalb des angegebenen Zeitraums ab.',
        },
      },
      maintenance_service: {
        title: 'Wartung & Service',
        question_1: {
          title: 'Kann ich die Wartungshistorie meiner Einheit einsehen?',
          body: 'Ja. Jede Einheit hat einen Tab "Wartungshistorie", in dem abgeschlossene Wartungen aufgelistet sind. Es werden nur Wartungen angezeigt, die von autorisierten Volvo Penta Händlern durchgeführt wurden. Wenn eine Wartung fehlt, wenden Sie sich bitte direkt an Ihren Händler.<br /><br />Wählen Sie eine Einheit aus und öffnen Sie den Tab "Wartungshistorie", um die Details einzusehen.',
          link: {
            href: 'https://www.volvopenta.com/connect/units',
            label: 'Übersicht der Einheiten',
          },
        },
        question_2: {
          title: 'Warum kann ich nicht sehen, welcher Händler eine Wartung durchgeführt hat?',
          body: 'Händlerdetails werden nur verifizierten Eigentümern der Einheit angezeigt.<br /><br />Um den Namen und den Standort des Händlers für eine abgeschlossene Wartung zu sehen, müssen Sie als Eigentümer registriert sein. Wenn Sie noch nicht verifiziert sind, wenden Sie sich bitte an Ihren autorisierten Volvo Penta Händler, um Ihre Eigentümerschaft zu bestätigen.',
        },
      },
      warranty_coverage: {
        title: 'Garantie & erweiterte Abdeckung',
        question_1: {
          title: 'Wie kann ich feststellen, ob meine Einheit(en) eine Garantie & Abdeckung haben?',
          body: 'Jede Einheit hat einen Tab "Garantie & Abdeckung", in dem alle aktiven Garantien angezeigt werden. Wählen Sie eine Einheit aus und öffnen Sie den Tab, um die Details zu sehen.',
          link: {
            href: 'https://www.volvopenta.com/connect/units',
            label: 'Übersicht der Einheiten',
          },
        },
        question_2: {
          title: 'Was ist die Standardgarantie?',
          body: 'Alle Volvo Penta Produkte beinhalten eine werkseitige Standardgarantie. Die Abdeckung gilt für einen definierten Zeitraum oder eine bestimmte Anzahl von Betriebsstunden – je nachdem, was zuerst eintritt – und hängt vom Produkt und seiner Anwendung ab.',
        },
        question_3: {
          title: 'Was ist die Standardgarantie ohne Stundenbegrenzung?',
          body: 'Einige Installationsarten haben während des ersten und/oder zweiten Jahres der Garantie keine Begrenzung der Betriebsstunden. Details hängen vom Produkt und der Installation ab und sind in den Garantiebedingungen festgelegt.',
        },
        question_4: {
          title: 'Was ist die Komponenten-Garantie?',
          body: 'Die Komponenten-Garantie deckt bestimmte Hauptkomponenten Ihres Volvo Penta Produkts ab. Die Dauer der Abdeckung und die Begrenzung der Betriebsstunden variieren je nach Komponente und sind in den Garantiebedingungen festgelegt.',
        },
        question_5: {
          title: 'Was ist die Emissionsgarantie?',
          body: 'Motoren, die nach den Vorschriften der US EPA oder California ARB zugelassen sind, umfassen die Emissionsgarantie. Diese gilt für einen definierten Zeitraum oder eine bestimmte Anzahl von Betriebsstunden, wie in den Bedingungen der Emissionsgarantie angegeben.',
        },
      },
      notification: {
        title: 'Benachrichtigungen',
        question_1: {
          title: 'Warum erhalte ich keine Benachrichtigungen über meine Einheit(en)?',
          body: 'Überprüfen Sie Ihre Benachrichtigungseinstellungen. Stellen Sie sicher, dass Benachrichtigungen auf der Einstellungsseite aktiviert sind.',
          link: { href: 'https://www.volvopenta.com/connect/settings', label: 'Einstellungen' },
        },
      },
    },
    login: {
      hero_title: 'Willkommen bei Volvo Penta Connect',
      hero_body:
        'Alles rund um Ihren Volvo-Penta-Motor oder Ihr Produkt an einem Ort. Handbücher, Bedienungsanleitungen, Ersatzteile, Rückrufe, Qualitätskampagnen, Garantiestatus und mehr.',
      log_in: 'Anmelden',
      blurb_back_button: 'Startseite',
      blurb_bolded_main_text:
        'Willkommen bei Volvo Penta Connect, der neuen Plattform für Flottenbetreiber und Freizeiteigner.',
      blurb_regular_main_text:
        'Registrieren Sie Ihren Motor, um auf relevante Informationen wie Handbücher, Rückrufe und Garantiestatus zuzugreifen. Die Registrierung stellt sicher, dass Ihr lokaler Händler Sie mit den richtigen Teilen und Services unterstützen kann. Und das Beste: Sie ist völlig kostenlos.',
      CTAs: {
        manuals_label: 'Bequemer Zugriff auf Handbücher',
        manuals_title: 'Alle Ihre Handbücher mit einem Klick',
        manuals_body:
          'Mit Volvo Penta Connect müssen Sie nicht mehr nach der Seriennummer suchen, um auf Handbücher zuzugreifen. Unsere Plattform bietet eine One-Click-Lösung und stellt Ihnen die vollständige Sammlung von Handbüchern und Bedienungsanleitungen für alle Ihre Motoren und Produkte zur Verfügung.',
        efficiency_label: 'Effizienz',
        efficiency_title: 'Schnell die richtigen Ersatzteile finden',
        efficiency_body:
          'Volvo Penta Connect bietet eine einfach zu bedienende Funktion, die den Prozess vereinfacht, das richtige Ersatzteil für Ihren Volvo-Penta-Motor zu finden – ganz ohne das lästige Suchen nach Seriennummer oder Motormodell.',
        service_label: 'Produktwartung nachverfolgen',
        service_title: 'Registrierte Servicehistorie Ihrer Produkte anzeigen',
        service_body:
          'Bleiben Sie mit Volvo Penta Connect über den Zustand Ihrer Produkte informiert. Erhalten Sie eine umfassende Übersicht, einschließlich Servicedetails, über die registrierte Servicehistorie all Ihrer Penta-Produkte.',
      },
      cards: {
        uptime_label: 'Betriebszeit sicherstellen',
        uptime_title: 'Erfahren Sie, ob Sie aktive Kampagnen haben',
        uptime_body:
          'Mit Volvo Penta Connect war es noch nie einfacher, den Überblick über Ihre Produktkampagnen zu behalten. Stellen Sie sicher, dass Ihre Produkte auf dem neuesten Stand sind und immer optimal funktionieren.',
        connect_label: 'Alle Ihre Garantien im Blick behalten',
        connect_title: 'Ihre Garantien in Volvo Penta Connect',
        connect_body:
          'Verschaffen Sie sich einen Überblick über all Ihre Garantien und Abdeckungen. Prüfen Sie, ob sie aktiv sind und wie lange sie gültig bleiben, und erhalten Sie Benachrichtigungen, wenn ihr Ablauf bevorsteht.',
      },
    },
    welcome: {
      title: 'Willkommen!',
      body: 'Um Volvo Penta Connect vollständig nutzen zu können, müssen Sie ein Boot oder eine Maschine in Ihrem Konto registrieren. Sobald dies geschehen ist, haben Sie Zugriff auf Handbücher und Bedienungsanleitungen, einfacheren Zugang zu relevanten Ersatzteilen, Garantiestatus und vieles mehr.',
      get_help: 'Hilfe erhalten',
      get_started: 'Loslegen',
      accordion: {
        who_is_it_for: {
          title: 'Für wen ist Volvo Penta Connect geeignet?',
          body: 'Volvo Penta Connect ist ein Kundenportal, in dem Sie einen Überblick über Ihre Einheit(en) und relevante Informationen wie Handbücher & Bedienungsanleitungen, Kampagnen, Garantiestatus und mehr erhalten.',
        },
        how_to_register: {
          title: 'Kann ich dieses Produkt ohne Registrierung einer Einheit nutzen?',
          body: 'Sie müssen eine Einheit in Ihrem Konto registrieren. Sobald eine Einheit hinzugefügt wurde, können Sie die Einheitsdaten überwachen, Over-the-Air-Software-Updates installieren und auf Handbücher und Bedienungsanleitungen zugreifen, die mit dieser Einheit verknüpft sind.',
        },
        can_i_use_without_registering: {
          title: 'Wo finde ich Handbücher & Bedienungsanleitungen?',
          body: 'Handbücher und Bedienungsanleitungen werden verfügbar, sobald eine Einheit zu Ihrem Konto hinzugefügt wurde, sodass wir Dokumentationen anzeigen können, die zu Ihrer spezifischen Einheit passen.',
        },
      },
    },
  },
  components: {
    modal: {
      accept_invitation_modal: {
        invite_title: 'Willkommen bei {{name}}!',
        invite_description:
          'Sie wurden in den Arbeitsbereich {{fleetName}} eingeladen. Hier können Sie den Zustand Ihrer Flotte und Einheiten überwachen, Analysen einsehen sowie Benachrichtigungen zu Kampagnen- und Garantieaktualisierungen erhalten.',
        accept: 'Einladung annehmen',
        dismiss: 'Einladung ablehnen',
      },
      add_units_fleet_modal: {
        button_add: 'Flotte erstellen',
        button_update: 'Flotte aktualisieren',
        title_add: 'Flotte erstellen',
        title_update: 'Flotte aktualisieren',
        title_success: 'Flotte erfolgreich erstellt',
        text_success:
          'Ihre Flotte wurde erstellt. Wenn die neue Flotte nicht in der Liste erscheint, aktualisieren Sie bitte die Seite.',
        fleet_name: 'Geben Sie den Flottennamen ein oder wählen Sie Ihre Flotte aus',
        select_your_fleet: 'Wählen Sie Ihre Flotte aus',
        click_to_create_fleet: 'Klicken Sie, um eine Flotte zu erstellen',
        table: {
          unit_name: 'Einheitenname',
          location: 'Standort',
          engines: 'Motoren',
          fleets: 'Flotten',
        },
        cancel: 'Vorerst überspringen',
        submit: {
          add_units: 'Einheit(en) zur Flotte hinzufügen',
          create_fleet: 'Flotte erstellen',
          close: 'Schließen',
        },
      },
      remove_units_fleet_modal: {
        button_remove: 'Aus der Flotte entfernen',
        title_remove: 'Einheiten aus der Flotte entfernen',
        description:
          'Sie sind dabei, die ausgewählten Einheiten aus der Flotte zu entfernen. Diese Aktion kann nicht rückgängig gemacht werden.',
        confirmation: 'Sind Sie sicher, dass Sie die ausgewählten Einheiten aus der Flotte entfernen möchten?',
        cancel: 'Abbrechen',
        submit: {
          create_fleet: 'Aus der Flotte entfernen',
        },
      },
      delete_fleet_modal: {
        title: 'Flotte entfernen',
        description:
          'Das Entfernen einer Flotte aus Ihrem Konto ist dauerhaft. Sie können eine Flotte jederzeit erneut hinzufügen.',
        confirmation: 'Sind Sie sicher, dass Sie die folgende Flotte entfernen möchten?',
        submit: 'Ja, Flotte aus Konto entfernen',
      },
      edit_fleet_modal: {
        title: 'Flotte bearbeiten',
        fleet_name: 'Flottenname',
        select_icon: 'Flottensymbol auswählen',
      },
      add_new_unit_modal: {
        button: 'Einheit hinzufügen',
        search_unit: {
          title: 'Finden Sie Ihre Einheit',
          description: 'Suchen Sie die Einheit, die Sie hinzufügen möchten, anhand der Motorseriennummer.',
          serial_number: 'Motorseriennummer',
          find_serial_number: 'So finden Sie Ihre Seriennummer',
          is_not_found: {
            label: 'Seriennummer existiert nicht in unseren Aufzeichnungen',
            body: 'Bitte stellen Sie sicher, dass Sie die richtige Seriennummer gefunden haben. Falls es weiterhin nicht funktioniert, kontaktieren Sie bitte Ihren Volvo Penta Händler oder finden Sie einen in unserem <a>Händlersuche</a>, um Hilfe zu erhalten.',
          },
          is_not_registered_industrial: {
            label: 'Einheit für dieses Produkt nicht registriert',
            body: 'Registrieren Sie die Einheit, um sie Ihrem Konto hinzuzufügen. Die Registrierungsanfrage wird von unserem Support-Team bearbeitet und die Einheit wird nach Abschluss hinzugefügt.',
          },
          is_not_registered_marine: {
            label: 'Einheit für dieses Produkt nicht registriert',
            body: 'Die Einheit muss registriert werden, damit Sie sie Ihrem Konto hinzufügen können. Bitte wenden Sie sich an Ihren Volvo Penta Händler oder finden Sie einen in unserer <a>Händlersuche</a>, um Hilfe bei der Registrierung der Einheit zu erhalten.',
          },
          is_maximum_units_reached: {
            label: 'Maximale Anzahl an Einheiten erreicht',
            body: 'Sie können bis zu drei nicht im Besitz befindliche Einheiten hinzufügen. Um weitere hinzuzufügen, müssen Sie als Eigentümer der Einheit registriert sein. Bitte wenden Sie sich an Ihren Volvo Penta Händler, um Hilfe zu erhalten, oder nutzen Sie unseren <a>Händlersuche</a>, um einen in Ihrer Nähe zu finden.',
          },
          is_unit_already_added: {
            label: 'Einheit bereits zu Ihrem Konto hinzugefügt',
            body: 'Diese Einheit ist bereits in Ihrem Konto verfügbar und muss nicht erneut registriert werden. Sie können sie in Ihrer Einheitenliste finden.',
          },
        },
        unit_information: {
          title: 'Einheitsinformationen eingeben',
          description:
            'Bitte füllen Sie die nachstehenden Angaben aus, um Ihre Einheit zu Volvo Penta Connect hinzuzufügen und relevante Informationen über Ihr Penta-Produkt zu erhalten.',
          privacy_information:
            'Volvo Penta erfasst und verwendet bestimmte personenbezogene Daten über Sie. Wenn Sie mehr darüber erfahren möchten, wie Volvo Penta Ihre personenbezogenen Daten verarbeitet, besuchen Sie bitte <a>Datenschutzinformationen zur Registrierung von Eigentümern oder Betreibern</a>.',
        },
        marine_unit_information: {
          serial_number: 'Motorseriennummer',
          serial_number_helperText: 'Die Volvo-Penta-Seriennummer befindet sich auf dem Motorschild.',
          vessel_name: 'Schiffsname',
          vessel_name_helperText: 'Der Name (z.B. Black Pearl), der Ihrem Schiff gegeben wurde.',
          vessel_id: 'Schiffs-ID',
          imo_number: 'IMO-Nummer',
          imo_number_helperText:
            'Internationale Seeschifffahrts-Organisationsnummer zur eindeutigen Identifikation Ihres Schiffes.',
          mmsi_number: 'MMSI-Nummer',
          mmsi_number_helperText: 'Maritime Mobile Service Identity-Nummer für die Funkkommunikation.',
          manufacturer: 'Hersteller',
          manufacturer_helperText: 'Der Hersteller Ihres Schiffes.',
          model: 'Modell',
          model_helperText: 'Das Modell des Schiffes.',
          vessel_segment: 'Schiffssegment',
          type_of_vessel: 'Schiffstyp',
          home_port: 'Heimathafen',
          home_port_helperText: 'Standort oder Heimathafen Ihres Schiffes',
          add_unit: 'Einheit hinzufügen',
        },
        marine_unit_added: {
          title: 'Ihre Einheit wurde hinzugefügt',
          description:
            'Ihr Schiff wurde hinzugefügt und Sie können die Informationen Ihres Schiffes einsehen, Handbücher herunterladen, Ersatzteile finden und mehr.',
          add_another_unit: 'Weitere Einheit hinzufügen',
          go_to_unit: 'Zur Einheit gehen',
        },
        industrial_contact_information: {
          title: 'Kontaktinformationen eingeben',
          description:
            'Bitte füllen Sie die nachstehenden Angaben zum Eigentümer aus, um Ihre Einheit hinzuzufügen. Ihre eingegebenen Daten werden manuell von unserem Support-Team geprüft, bevor Sie als Eigentümer registriert werden.',
          proof_of_ownership: 'Eigentumsnachweis',
          proof_of_ownership_helperText:
            'Ein Eigentumsnachweis (z.B. Vertrag, Quittung oder Auftragsbestätigung) ist erforderlich, um das Unternehmen als Eigentümer der Maschine zu registrieren. Falls Sie keinen Eigentumsnachweis vorlegen können, wenden Sie sich bitte an Ihren Händler.',
          proof_of_ownership_upload:
            'Ziehen Sie Dateien per Drag & Drop hierher oder klicken Sie, um Dateien zu durchsuchen, PDF-, PNG-, JPG- oder HEIC-Dateien mit jeweils max. 3 MB.',
          proof_of_ownership_qr: 'Eigentum durch QR-Code bestätigt',
          contact_person: 'Kontaktperson',
          contact_person_description:
            'Sie werden als Kontaktperson für das Unternehmen und diese Maschine registriert.',
          first_name: 'Vorname',
          last_name: 'Nachname',
          email: 'E-Mail',
          company_details: 'Unternehmensdetails',
          company_details_description: 'Geben Sie die Informationen des Unternehmens ein, dem die Maschine gehört.',
          company_name: 'Firmenname',
          company_name_helperText: 'Das Unternehmen, dem die Maschine gehört.',
          vat_number: 'USt-IdNr.',
          address_line: 'Adresse',
          zip_code: 'Postleitzahl',
          city: 'Stadt',
          state: 'Bundesland / Provinz',
          country: 'Land',
          notification: 'Benachrichtigungseinstellungen',
        },
        industrial_unit_information: {
          engine_serial_number: 'Motorseriennummer',
          engine_serial_number_helperText:
            'Wenn Ihre Maschine mehr als einen Motor hat, klicken Sie, um ein weiteres Feld hinzuzufügen und die Seriennummer des zusätzlichen Motors einzugeben.',
          add_engine: 'Motor hinzufügen',
          machine_name: 'Maschinenname',
          machine_name_helperText:
            'Der Name (z.B. T4, Einheit 11) Ihrer Maschine, Ihres Fahrzeugs oder Stromerzeugers.',
          machine_id: 'Maschinen-ID',
          machine_id_helperText:
            'Die eindeutige Identifikationsnummer, die vom Originalgerätehersteller (OEM) bereitgestellt wird.',
          manufacturer: 'Hersteller',
          manufacturer_helperText:
            'Der Hersteller Ihrer Maschine, Ihres Fahrzeugs oder Stromerzeugers (z.B. Magni, Inmesol, Atlas).',
          model: 'Modell',
          model_helperText: 'Das Modell Ihrer Maschine, Ihres Fahrzeugs oder Stromerzeugers.',
          machine_segment: 'Maschinensegment',
          machine_segment_helperText:
            'Das Segment, in dem sich Ihre Maschine befindet (z.B. Materialhandling, Bergbau, Forstwirtschaft).',
          type_of_machine: 'Maschinentyp',
          type_of_machine_helperText: 'Welcher Maschinentyp es ist (z.B. Terminaltraktor, Harvester, Gabelstapler).',
          operating_location: 'Einsatzort',
          operating_location_helperText: 'Standort oder Einsatzort Ihrer Maschine.',
        },
        industrial_unit_added: {
          title: 'Einheit hinzugefügt',
          description:
            'Ihre Maschine wurde hinzugefügt und Sie können die Informationen Ihrer Maschine einsehen, Handbücher herunterladen, Ersatzteile finden und mehr.',
          manual_ownership_registration: 'Manuelle Eigentumsregistrierung',
          manual_ownership_registration_description:
            'Registrierung und Eigentumskontrolle werden manuell von unserem Support-Team durchgeführt. <b>Derzeit besteht eine hohe Nachfrage, was zu langen Bearbeitungszeiten führt, rechnen Sie mit 2-6 Wochen.</b> Sie erhalten eine Bestätigungs-E-Mail, wenn Ihre Anfrage genehmigt wurde.',
          add_another_unit: 'Weitere Einheit hinzufügen',
          go_to_unit: 'Zur Einheit gehen',
        },
        industrial_no_unit_added: {
          title: 'Maschine wird nicht in Volvo Penta Connect angezeigt',
          description:
            'Ihre Maschine existiert nicht in unseren Aufzeichnungen. Sie wird nicht in Volvo Penta Connect angezeigt, bis der manuelle Registrierungsprozess durch unser Support-Team abgeschlossen ist.',
          manual_registration: 'Manuelle Registrierung',
          manual_registration_description:
            'Registrierung und Eigentumskontrolle werden manuell von unserem Support-Team durchgeführt. <b>Derzeit besteht eine hohe Nachfrage, was zu langen Bearbeitungszeiten führt, rechnen Sie mit 2-6 Wochen.</b> Sie erhalten eine Bestätigungs-E-Mail, wenn Ihre Anfrage genehmigt wurde.',
          manuals_campaigns: 'Handbücher, Teile und Kampagnen',
          manuals_campaigns_description:
            'Wenn Sie nach Handbüchern und Anleitungen, Ersatzteilen oder Kampagnen suchen – hier sind Links, über die Sie diese erhalten können, bis die Registrierung abgeschlossen ist. Sie haben diese Informationen auch in Ihrem E-Mail-Postfach erhalten.',
          add_another_unit: 'Weitere Einheit hinzufügen',
        },
      },
      invite_members_modal: {
        button: 'Mitglieder hinzufügen',
        title: 'Ausstehende Einladungslinks',
        empty_state: {
          title: 'Keine Einladungen gefunden.',
          message:
            'Versuchen Sie, mit der Schaltfläche unten eine Einladung zu erstellen und in Ihre Zwischenablage zu kopieren.',
        },
        generate_invitation_link: 'Neue Einladung erstellen und in Zwischenablage kopieren',
      },
      remove_members_modal: {
        title: 'Mitglied(er) entfernen',
        description:
          'Das Entfernen eines Mitglieds vom Konto {{count}} ist dauerhaft. Sie können ein Mitglied jederzeit erneut einladen.',
        are_you_sure: 'Sind Sie sicher, dass Sie die folgenden Mitglied(er) entfernen möchten?',
        cancel: 'Abbrechen',
        submit: 'Ja, Mitglied(er) vom Konto entfernen',
      },
      add_network_modal: {
        button: 'Netzwerk hinzufügen',
        title: 'Netzwerk hinzufügen',
        refresh_network: 'Netzwerk aktualisieren',
        edit_network: 'Netzwerk bearbeiten',
        remove_network: 'Netzwerk entfernen',
        remove_network_description:
          'Entfernen Sie das Netzwerk aus Ihrer gespeicherten Liste für dieses Fahrzeug / Schiff. Das Entfernen des Netzwerks ist dauerhaft. Sie können das Netzwerk jederzeit erneut hinzufügen.',
        network_name: 'Netzwerkname (SSID)',
        network_name_helperText: 'Netzwerk-ID des WLAN-Routers',
        password: 'Passwort',
        password_helperText: 'Passwort des WLAN-Routers',
        error_title: 'Etwas ist schiefgelaufen',
        error_description: 'Das Netzwerk konnte nicht aktualisiert werden. Bitte versuchen Sie es später erneut.',
        priority: 'Priorität',
      },
      wifi_polling_modal: {
        title: 'Updating Network Settings',
        title_error: 'Network Settings Update',
        connecting: 'Connecting to your device...',
        searching: 'Searching for your network settings...',
        almost: 'Almost there, fetching configuration...',
        hang_tight: 'Still working on it, hang tight...',
        communicating: 'Communicating with the gateway...',
        moment: 'Just a moment longer...',
        timeout: 'Unable to reach your device. Please try again later.',
        error: 'Something went wrong while updating network settings. Please try again.',
      },
      wifi_error_log_modal: {
        title: 'Fehlerprotokoll',
        no_errors: 'Keine Fehler gefunden',
        close: 'Schließen',
      },
      digital_services_modal: {
        activate_service_content: {
          title: 'Dienst aktivieren',
          description:
            'Damit wir den richtigen Aktivierungsvertrag vorbereiten und die richtigen Kontaktdaten anfordern können, geben Sie bitte an, in wessen Namen Sie diesen Dienst aktivieren.',
          myself: 'Ich selbst',
          myself_helper: 'Aktivieren Sie diesen Dienst für Ihre private Maschine oder Ihr privates Schiff.',
          company: 'Ein Unternehmen',
          company_helper:
            'Aktivieren Sie diesen Dienst im Namen eines Unternehmens, einer Flotte oder einer Organisation.',
        },
        contract_details_content: {
          title: 'Kontaktdaten',
          first_name: 'Vorname',
          last_name: 'Nachname',
          email_address: 'E-Mail-Adresse',
          company_name: 'Firmenname',
          company_details_title: 'Firmendaten',
          company_details_description: 'Geben Sie die Daten des Unternehmens ein, dem die Maschine gehört.',
          vat_number: 'USt-IdNr.',
          social_security_number: 'Sozialversicherungsnummer',
          state_province: 'Bundesland/Provinz',
          country: 'Land',
          address: 'Adresse',
          city: 'Stadt',
          postal_code: 'Postleitzahl',
          is_correct_info: 'Ich bestätige hiermit, dass die oben angegebenen Informationen wahr und korrekt sind.',
          is_terms_checked:
            'Ich erkenne an und bestätige, dass ich die Allgemeinen Geschäftsbedingungen der Vereinbarung über digitale Dienste (einschließlich Anhänge) gelesen und verstanden habe und dass ich berechtigt bin, den genannten <a>Vertrag</a> mit rechtsverbindlicher Wirkung zu akzeptieren.',
          data_management_agreement: 'Ich habe auch die <a>Datenverwaltungsvereinbarung</a> gelesen und verstanden.',
          remote_software_updates_title: 'Remote Software Updates',
          service_description_for: 'Servicebeschreibung für {{serviceName}}',
          is_service_details_checked:
            'Ich erkenne an und bestätige, dass ich die <serviceDescription /> gelesen und verstanden habe und dass ich berechtigt bin, die genannten Serviceinformationen zu akzeptieren.',
          is_data_act_checked:
            'Das EU-Datengesetz verlangt, dass wir Ihnen vorvertragliche Informationen zur Verfügung stellen, die beschreiben, wie ein Volvo Penta Connected Product (auf dem Markt in einem EU-Mitgliedstaat) Produktdaten erzeugt, wie die Produktdaten gesammelt werden, wie Sie als Benutzer darauf zugreifen können und wie sie von Volvo Penta verwendet werden. Die vollständigen Informationen sind unter <a>EU Data Act | Volvo Penta</a> verfügbar. Indem Sie dieses Kästchen ankreuzen und mit dem Kauf, der Miete oder der Vermietung des Volvo Penta Connected Product fortfahren, bestätigen Sie, dass Ihnen der Zugang zu diesen vorvertraglichen Informationen gemäß dem EU-Datengesetz bereitgestellt wurde.',
        },
        choose_receiver_content: {
          title: 'Wählen Sie einen Händler',
          description:
            'Bitte fügen Sie einen Händler hinzu, um die Alarmbenachrichtigungen zu erhalten. Dies gewährleistet maximale Betriebszeit und die effizienteste Serviceplanung für Ihr Volvo Penta-Produkt.',
          find_nearest_dealer: 'Finden Sie Ihren nächstgelegenen Händler',
          dealer: 'Händler',
          dealer_placeholder: 'Geben Sie den Namen des Händlers und/oder dessen E-Mail-Adresse ein',
        },
        thanks_content: {
          title: 'Danke!',
          description:
            'Bitte beachten Sie, dass die Aktivierung nicht sofort erfolgt und einige Zeit in Anspruch nehmen wird. Wir benachrichtigen Sie per E-Mail, sobald der Service einsatzbereit ist.',
          description_all_services: 'Wir benachrichtigen Sie per E-Mail, sobald die Dienste einsatzbereit sind.',
          Marine: {
            description:
              'Bitte beachten Sie, dass die Aktivierung nicht sofort erfolgt und einige Zeit in Anspruch nehmen kann. Wir benachrichtigen Sie per E-Mail, sobald die Aktivierung abgeschlossen ist.',
          },
        },
        deactivate_service: {
          title: 'Service kündigen',
          description: 'Sind Sie sicher, dass Sie den Service kündigen möchten?',
          description_all: 'Sind Sie sicher, dass Sie alle Dienste kündigen möchten, ({{serviceNames}})?',
          deactivate: 'Kündigen',
        },
      },
    },
    table: {
      items_selected: '{{selectedRowsCount}} Elemente ausgewählt',
      page: 'Seite {{currentPage}} von {{totalPages}}',
      unit_table: {
        unit_name: 'Einheitenname',
        model_brand: 'Modell/marke',
        location: 'Standort',
        engines: 'Motoren',
        operating_hours: 'Betriebsstunden',
        fleets: 'Flotten',
        campaigns: 'Kampagnen',
        digital_services: 'Digitale dienste',
        unverified_ownership: 'Unbestätigter besitz',
      },
      campaigns_table: {
        campaign: 'Kampagne',
        download: 'Herunterladen',
        created_date: 'Erstellungsdatum',
        valid_to: 'Gültig bis',
        product: 'Produkt',
        type: 'Typ',
      },
      members_table: {
        name: 'Name',
        email: 'E-Mail',
        account: 'Konto',
      },
      invitations_table: {
        creation_date: 'Erstellungsdatum',
      },
      specifikationer_table: {
        driveline: 'Antriebsstrang',
        product_type: 'Produkttyp',
        product: 'Produkt',
        serial_number: 'Seriennummer',
        operating_hours: 'Betriebsstunden',
        engine: 'Motor',
        segment: 'Segment',
        rated_power: 'Nennleistung',
        rated_speed: 'Nenndrehzahl',
      },
      network_settings_table: {
        priority: 'Priorität',
        ssid: 'SSID',
        frequency: 'Frequenz',
        last_updated: 'Zuletzt aktualisiert',
        connection: 'Verbindung',
        connected: 'Verbunden',
        not_connected: 'Nicht verbunden',
        lost_connection: 'Verbindung unterbrochen',
        disconnected: 'Getrennt',
      },
      coverage_table: {
        type: 'Typ',
        description: 'Garantie-/Abdeckungsbeschreibung',
        expiration_hours: 'Ablaufstunden',
        expiration_date: 'Ablaufdatum',
      },
      fault_codes_table: {
        unit: 'Einheit',
        severity: 'Schweregrad',
        fault_code: 'Fehlercode',
        fault_description: 'Fehlerbeschreibung',
        failure_event: 'Fehlerereignis',
        timestamp: 'Zeitstempel',
        operating_hours: 'Betriebsstunden',
        occurrences: 'Vorkommen',
      },
      connectivity_table: {
        unit: 'Einheit',
        operating_hours: 'Betriebsstunden',
        last_seen: 'Zuletzt gesehen',
        status: 'Konnektivitätsstatus',
      },
      fuel_consumption_table: {
        unit: 'Einheit',
        chassis_id: 'Fahrgestellnummer',
        operating_hours: 'Betriebsstunden',
        fuel_efficiency: 'Kraftstoffeffizienz (l/h)',
        total_fuel: 'Gesamtverbrauch (l)',
      },
      publications_table: {
        title: 'Titel',
        description: 'Beschreibung',
        designation: 'Produkt',
        product: 'Produkttyp',
        size: 'Größe',
        languageCode: 'Spr.',
        buy: 'Kaufen',
      },
      maintenance_dealer_table: {
        header: 'Händler',
        dealer_name: 'Händlername',
        location: 'Standort',
      },
      maintenance_protocol_details_table: {
        header: 'Details zum Serviceprotokoll',
        title: 'Titel',
        current_service: 'Aktueller service',
        previous_service: 'Vorheriger service',
        date: 'Datum',
        operating_hours: 'Betriebsstunden',
        unit_age: 'Einheitenalter',
        months: 'Monate',
      },
      maintenance_driveline_table: {
        header: 'Antriebsstrang {{driveline}}',
        name: 'Name',
        serial_number: 'Seriennummer',
        product: 'Produkt',
      },
      maintenance_service_table: {
        header: 'Service {{service}}',
        activity: 'Aktivität',
        detail: 'Detail',
        last_performed: 'Zuletzt durchgeführt',
        performed: 'Durchgeführt',
        complete: 'Abgeschlossen',
        not_performed: 'Nicht durchgeführt',
      },
    },
    tooltip: {
      copy_clipboard: 'In Zwischenablage kopieren',
    },
    language_selector: {
      de: 'Sprache - Deutsch',
      en: 'Language - English',
      fr: 'Langue - Français',
      it: 'Lingua - Italiano',
      nl: 'Taal - Nederlands',
      pt: 'Idioma - Português',
      sv: 'Språk - Svenska',
    },
    main_menu: {
      home: 'Startseite',
      settings: 'Einstellungen',
      support: 'Support',
    },
    user_menu: {
      title: 'Benutzermenü',
      account_settings: 'Kontoeinstellungen',
      logout: 'Abmelden',
      switch_fleet_account: 'Flottenkonto wechseln',
    },
    footer: {
      useful: 'Nützlich',
      manuals_and_handbooks: 'Handbücher & Anleitungen',
      dealer_locator: 'Händlersuche',
      warranty: 'Garantie',
      parts_column: 'Teile und Zubehör',
      parts: 'Teile',
      remanufactured: 'Überholte Komponenten und Motoren',
      contact: 'Kontakt',
      action_service: 'Volvo Penta Action Service',
      faq: 'FAQ',
      contact_us: 'Kontaktieren Sie uns',
      privacy_policy: 'Datenschutzrichtlinie',
      cookie_policy: 'Cookie-Richtlinie',
    },
    cta: {
      members_fleetaccount_cta: {
        heading: 'Andere zu Ihrem Konto einladen',
        message:
          'Fügen Sie Mitglieder hinzu, um den Zugriff auf Ihre Einheiten und Flotten zu teilen. Sie können Kollegen, Geschäftspartner oder Familienmitglieder einladen und Rollen zuweisen, um zu steuern, was diese sehen und verwalten können. Kopieren Sie den Link und senden Sie ihn an Ihr Mitglied.',
      },
      members_digitalaccount_cta: {
        heading: 'Andere zu Ihrem Konto einladen',
        message:
          'Das Einladen von Mitgliedern erfordert einen verifizierten Einheitenbesitzer. Sobald Ihr Besitz bestätigt wurde, können Sie Kollegen, Geschäftspartner oder Familienmitglieder einladen und Rollen zuweisen, um zu steuern, was diese sehen und verwalten können.<br /><br />Um den Besitz zu verifizieren, wenden Sie sich bitte an Ihren autorisierten Volvo Penta Händler.',
      },
      action_cta: {
        heading: 'Wie man handelt',
        message: 'Um eine Kampagne durchzuführen, wenden Sie sich an Ihren Volvo Penta- oder OEM-Händler.',
        find_dealer: 'Finden Sie Ihren nächstgelegenen Händler',
        learn_more: 'Erfahren Sie mehr',
      },
      notifications_cta: {
        heading: 'Benachrichtigungen',
        message:
          'Bleiben Sie auf dem Laufenden und erhalten Sie E-Mail-Benachrichtigungen, wenn für eine Einheit eine installierbare Kampagne verfügbar ist.',
        notification_settings: 'Benachrichtigungseinstellungen',
      },
    },
    role_types: {
      title: 'Rolleninformationen',
      manager: {
        title: 'Manager',
        description:
          'Manager haben vollständige administrative Kontrolle über die Flotte, überwachen den Zustand der Einheiten und verwalten alle Einheiten, Einstellungen und Benutzerzugriffe.',
      },
      technician: {
        title: 'Technician',
        description:
          'Fokussiert auf den Zustand und die Wartung der Einheiten. Techniker können Einheitendaten überwachen und Remote-Software-Updates durchführen. Diese Rolle umfasst keine Berechtigungen zur Flotten- oder Benutzerverwaltung.',
      },
      viewer: {
        title: 'Viewer',
        description:
          'Betrachter haben nur Lesezugriff. Sie können den Status der Einheiten überwachen und Berichte einsehen, aber keine Einstellungen oder Konfigurationen ändern.',
      },
    },
    campaign_types: {
      title: 'Kampagnentypen verstehen',
      recall: {
        title: 'Rückrufkampagne',
        description:
          'Sicherheitsrelevante nachträgliche Produktionsänderung; muss sofort umgesetzt werden, um den sicheren Betrieb des Produkts zu gewährleisten.',
      },
      quality: {
        title: 'Qualitätskampagne',
        description:
          'Nachträgliche Produktionsänderung; sollte so früh wie möglich umgesetzt werden, um den ordnungsgemäßen Betrieb des Produkts zu gewährleisten.',
      },
      service: {
        title: 'Servicekampagne',
        description:
          'Nachträgliche Produktionsänderung; sollte beim nächsten planmäßigen Service umgesetzt werden, um den ordnungsgemäßen Betrieb des Produkts zu gewährleisten.',
      },
      installation: {
        title: 'Installation',
        description:
          'Ein Remote-Software-Update ist bereit, von Ihnen mit der Volvo Penta Anwendung auf Ihrer Einheit installiert zu werden.',
        app_store: 'Für iOS im App Store herunterladen',
        google_play: 'Für Android bei Google Play herunterladen',
      },
    },
  },
};

export default deTranslation;
